// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import { queryClient } from './config/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import Login from './pages/Login';
import ProvisioningSurface from '../provisioning';

/**
 * Root of the Qcells-internal provisioning build.
 *
 * Mirrors `App.tsx`'s provider order — `QueryClientProvider` between
 * `MsalProvider` (index.tsx) and `BrowserRouter`, with `AuthProvider` inside
 * the router because it fetches. It lives here rather than under
 * `provisioning/` because the import boundary keeps `contexts/` and `config/`
 * out of that subtree.
 *
 * This build is selected in index.tsx by REACT_APP_SURFACE and deploys to its
 * own Static Web App with its own Entra app registration. It is deliberately
 * NOT a route inside App.tsx: that would ship it in the customer bundle behind
 * the customer's app registration.
 */
export default function ProvisioningApp() {
  // Dark only, pinned for the whole build — including /login, which renders
  // outside ProvisioningShell. Helios and Admin Center let the user toggle
  // g100/g10 through the shell store; this app deliberately does not.
  //
  // Two writes, because the attribute and the class drive different token
  // layers: `data-carbon-theme` on <html> for Helios' own tokens
  // (styles/helios-theme.scss), `.cds--g100` on <body> for Carbon's `--cds-*`
  // dark set, which @carbon/styles ships under that class with the white set
  // on :root. Without the class every Carbon component renders light inside
  // the dark chrome.
  useEffect(() => {
    document.documentElement.setAttribute('data-carbon-theme', 'g100');
    document.body.classList.add('cds--g100');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          {/* /login must exist here: the auth gate redirects unauthenticated
              visitors to it, and only the customer AppRoutes mounts it
              otherwise — without this route a first visit is a blank page. */}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<ProvisioningSurface />} />
          </Routes>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}
